import type { FxRateContract } from '@luxuryescapes/contract-fx';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { deriveOpportunityCurrency } from '../inclusions/inclusionsModel';

import type { PropertySetupValues } from './types';

// Minimal structural view of App.OpportunityPage for the fields this model reads.
// Keeping it structural keeps `derivePropertySetup` pure and easy to test without
// constructing a full opportunity page fixture.
export interface PropertySetupOpportunity {
  location?: { destination__c?: string | null; sub_destination__c?: string | null } | null;
}

export interface DerivePropertySetupInput {
  opportunity?: PropertySetupOpportunity | null;
  deals?: Array<OpportunityRelatedDeal>;
  roomAssets?: Array<OpportunityRoomAsset>;
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function minDate(dates: Array<string | null | undefined>): string | null {
  const valid = dates.filter((value): value is string => Boolean(value));
  if (valid.length === 0) {
    return null;
  }

  return valid.slice().sort()[0];
}

function maxDate(dates: Array<string | null | undefined>): string | null {
  const valid = dates.filter((value): value is string => Boolean(value));
  if (valid.length === 0) {
    return null;
  }

  return valid.slice().sort()[valid.length - 1];
}

// Pre-fills Property Setup from opportunity-related records. Sources are confirmed
// in useGetOffer.ts / useGetOpportunityRelated.ts (see plan XTRNT-1003):
//   propertyName    - first room asset's account__name
//   offerCountry    - opportunity location destination__c (fallback null when absent)
//   subDestination  - opportunity location sub_destination__c (fallback null when absent)
//   guestsPerRoom   - first room asset's included_number_of_adults__c (coerced to number)
//   travelFrom      - earliest deal travel_start_date__c
//   travelTo        - latest deal travel_end_date__c
//   currency        - reuse deriveOpportunityCurrency (first deal currencyisocode, AUD default)
//   fxRate          - left null; the hook auto-populates once FX rates load
//   prvReqLeadPrice  - left null; manual entry (no pre-fill source identified)
export function derivePropertySetup({
  opportunity,
  deals = [],
  roomAssets = [],
}: DerivePropertySetupInput): PropertySetupValues {
  const firstAsset = roomAssets.find((asset) => Boolean(asset?.account__name));

  return {
    propertyName: firstAsset?.account__name ?? null,
    offerCountry: opportunity?.location?.destination__c ?? null,
    subDestination: opportunity?.location?.sub_destination__c ?? null,
    guestsPerRoom: toNullableNumber(firstAsset?.included_number_of_adults__c),
    travelFrom: minDate(deals.map((deal) => deal.travel_start_date__c)),
    travelTo: maxDate(deals.map((deal) => deal.travel_end_date__c)),
    currency: deriveOpportunityCurrency(deals),
    fxRate: null,
    prvReqLeadPrice: null,
  };
}

// Resolves the AUD -> currency FX rate from contract-fx `/api/fx/rates` results.
// AUD is the base currency so it always resolves to 1. Unknown currencies resolve to
// null so the tab can leave the field blank for manual entry.
export function resolveFxRate(
  rates: Array<FxRateContract.FxRate> | undefined,
  currency: string | null | undefined,
): number | null {
  if (!currency) {
    return null;
  }

  if (currency === 'AUD') {
    return 1;
  }

  return rates?.find((rate) => rate.currency === currency)?.rate ?? null;
}
